import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { Button, Divider, FormLabel, Modal, PageSpinner } from "@impero-com/ui";
import { Row, Col } from "antd";
import memoizeOne from "memoize-one";

import { getCultures } from "api/generated/constants/constants";
import type { UserProfile } from "api/generated/model";
import {
    AvailableActions,
    CreateUserData,
    Culture,
    OrganizationInfo,
    UserOverview,
    UserWithSettingsResource,
} from "api/generated/model";
import { getOrgInfo } from "api/generated/organization/organization";
import { getUsers, postUsers } from "api/generated/users/users";
import { CheckboxInput } from "components/form/legacyFields/checkboxInput";
import {
    SelectInput,
    SelectInputOption,
} from "components/form/legacyFields/selectInput";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TimezoneTypeahead } from "components/timezoneTypeahead/timezoneTypeahead";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    subForm,
    simpleMappers,
    mapForm,
    ObjectError,
    emailValidator,
} from "utils/form";
import { getCultureInformationByName } from "utils/locale";
import { toUserOverview } from "utils/user";

import RoleAssignment from "./roleAssignment";

interface UserCreatorFormProps extends FormRootComponentProps<CreateUserData> {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
    availableActions: AvailableActions;

    onCancel: () => void;
    onUserCreated: (user: UserOverview) => void;
}

type UserCreatorStatus = { type: "DEFAULT" } | { type: "SUBMITTING" };

interface UserCreatorFormState {
    status: UserCreatorStatus;
}

const PASSWORD_AUTHENTICATION_ENABLED_VALUE = "enabled";
const INVITE_USER_VALUE = "invite";

class UserCreatorForm extends React.PureComponent<
    UserCreatorFormProps,
    UserCreatorFormState
> {
    private cultureOptions = memoizeOne(
        (cultures: Array<Culture>): Array<SelectInputOption> => {
            return cultures.map((c) => {
                const cultureInfo = getCultureInformationByName(c.name);
                return cultureInfo
                    ? { label: cultureInfo.label, value: cultureInfo.name }
                    : { label: c.name, value: c.name };
            });
        }
    );

    asyncComponent =
        React.createRef<AsyncComponent2<Culture[], OrganizationInfo>>();

    state: UserCreatorFormState = {
        status: { type: "DEFAULT" },
    };

    userHasAccess = () => this.props.availableActions.user.user.create;

    render = () => {
        return this.userHasAccess() ? this.renderMain() : <NoAccessCard />;
    };

    renderMain = () => {
        const { ajaxManager } = this.props;
        return (
            <AsyncComponent2
                key={`create-user-modal`}
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                promises={[getCultures, getOrgInfo]}
                renderLoading={() => <PageSpinner />}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderContent={this.renderPopup}
            />
        );
    };

    renderPopup = (cultures: Culture[], orgInfo: OrganizationInfo) => {
        // TODO: Add support for Locale enum, currently any string is accepted
        let cultureOptions: SelectInputOption[] = [];
        cultureOptions = this.cultureOptions(cultures);
        const { form, availableActions, onCancel, orgProfileCookie } =
            this.props;
        const { status } = this.state;
        const { assignableRoles } = availableActions;
        const user = subForm("user", form);
        const settings = subForm("settings", form);
        const { getFieldsError, getFieldDecorator } = form;

        const passwordAuthenticationDisabledMapper = simpleMappers<
            "passwordAuthenticationDisabled",
            boolean,
            string[],
            CreateUserData
        >(
            "passwordAuthenticationDisabled",
            (b: boolean) => (!b ? [PASSWORD_AUTHENTICATION_ENABLED_VALUE] : []),
            (a: string[]) => !a.includes(PASSWORD_AUTHENTICATION_ENABLED_VALUE)
        );
        const settingsForm2 = mapForm<{
            passwordAuthenticationDisabled: string[];
        }>()(settings, passwordAuthenticationDisabledMapper());

        const inviteUserMapper = simpleMappers<
            "inviteUser",
            boolean,
            string[],
            CreateUserData
        >(
            "inviteUser",
            (b: boolean) => (b ? [INVITE_USER_VALUE] : []),
            (a: string[]) => a.includes(INVITE_USER_VALUE)
        );
        const form2 = mapForm<{ inviteUser: string[] }>()(
            form,
            inviteUserMapper()
        );

        const passwordAuthenticationDisabled =
            settings.getFieldValue("passwordAuthenticationDisabled") ??
            orgInfo.defaultDisablePasswordAuthentication;

        return (
            <Modal
                variant="content"
                title={t("Users_CreateNewUser")}
                open
                width={675}
                footer={
                    <>
                        <Button id="create-user-back" onClick={onCancel}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            id="create-user-submit"
                            variant="primary"
                            loading={status.type === "SUBMITTING"}
                            onClick={this.save}
                            disabled={hasError(getFieldsError() as ObjectError)}
                        >
                            {t("User_CreateTitle")}
                        </Button>
                    </>
                }
                onClose={onCancel}
            >
                <Form>
                    <Row gutter={16}>
                        <Col span={12}>
                            <StringInput
                                type="CREATE"
                                form={user}
                                id="firstname"
                                label={t("MyProfile_UserDetails_FirstName")}
                                initialValue=""
                                validators={[
                                    {
                                        kind: "ruleset",
                                        required: true,
                                        whitespace: true,
                                    },
                                ]}
                            />
                        </Col>
                        <Col span={12}>
                            <StringInput
                                type="CREATE"
                                form={user}
                                id="lastname"
                                label={t("MyProfile_UserDetails_LastName")}
                                initialValue=""
                                validators={[
                                    {
                                        kind: "ruleset",
                                        required: true,
                                        whitespace: true,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                    <StringInput
                        type="CREATE"
                        form={user}
                        id="email"
                        label={t("MyProfile_UserDetails_Email")}
                        initialValue=""
                        validators={[
                            {
                                kind: "ruleset",
                                required: true,
                                whitespace: true,
                            },
                            {
                                kind: "custom",
                                validator: emailValidator,
                            },
                            {
                                kind: "async",
                                asyncValidator: this.checkEmailAvailability,
                            },
                        ]}
                    />
                    <Divider />
                    <Row gutter={16}>
                        <Col span={12}>
                            <SelectInput
                                id="culture"
                                form={user}
                                label={t(
                                    "RegisterUser_UpdateUserDetails_Language"
                                )}
                                type="CREATE"
                                initialValue={orgProfileCookie?.culture || "en"}
                                options={cultureOptions}
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel>{t("Timezone")}</FormLabel>
                            {user.getFieldDecorator("timezoneName", {
                                trigger: "", // Update handled by calls to setFieldsValue
                                valuePropName: "value",
                                initialValue:
                                    orgProfileCookie?.timezone ||
                                    "Europe/Copenhagen",
                            })(
                                <TimezoneTypeahead
                                    id="timezoneName"
                                    onSelect={(timezoneName) => {
                                        user.setFieldsValue({ timezoneName });
                                    }}
                                />
                            )}
                        </Col>
                    </Row>

                    <Divider />
                    <CheckboxInput
                        id="passwordAuthenticationDisabled"
                        label={t("CreateUser_Authentication")}
                        type="CREATE"
                        form={settingsForm2}
                        options={[
                            {
                                label: t(
                                    "CreateUser_PasswordAuthenticationEnabled"
                                ),
                                value: PASSWORD_AUTHENTICATION_ENABLED_VALUE,
                            },
                        ]}
                        validators={[{ kind: "ruleset", type: "boolean" }]}
                        columns={1}
                        initialValue={
                            orgInfo.defaultDisablePasswordAuthentication
                                ? []
                                : [PASSWORD_AUTHENTICATION_ENABLED_VALUE]
                        }
                    />
                    <Divider />
                    <FormLabel>{t("User_Roles_Title")}</FormLabel>
                    {getFieldDecorator("roles", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        valuePropName: "roles",
                        initialValue: [],
                    })(
                        <RoleAssignment
                            assignableRoles={assignableRoles}
                            onChangeRole={(role, selected) => {
                                let roles = form.getFieldValue("roles") || [];
                                if (selected) {
                                    if (!(roles.indexOf(role) >= 0)) {
                                        roles.push(role);
                                    }
                                } else {
                                    roles = roles.filter((r) => r !== role);
                                }
                                form.setFieldsValue({ roles });
                            }}
                        />
                    )}
                    <Divider />
                    {!passwordAuthenticationDisabled ? (
                        <CheckboxInput
                            id="inviteUser"
                            type="CREATE"
                            form={form2}
                            options={[
                                {
                                    label: t("CreateUser_Invite"),
                                    value: INVITE_USER_VALUE,
                                    disabled: false,
                                },
                            ]}
                            validators={[{ kind: "ruleset", type: "boolean" }]}
                            columns={1}
                            initialValue={[INVITE_USER_VALUE]}
                        />
                    ) : (
                        <CheckboxInput
                            type="READ_ONLY"
                            options={[
                                {
                                    label: t("CreateUser_Invite"),
                                    value: INVITE_USER_VALUE,
                                    disabled: true,
                                },
                            ]}
                            columns={1}
                        />
                    )}
                </Form>
            </Modal>
        );
    };

    checkEmailAvailability = (email: string): Promise<void> => {
        return getUsers({ format: "Overview", nameOrEmail: email }).then(
            (response) => {
                if (response.data.type !== "Overview") {
                    throw new Error("Expected an overview user list");
                }
                const matched = response.data.data.content.map(
                    (dto) => dto.resource
                );
                const emailLowerCase = email.toLowerCase();
                const exists = matched.some(
                    (user) => user.email.toLowerCase() === emailLowerCase
                );
                if (exists) {
                    return Promise.reject(
                        new Error(t("User_Validation_AlreadyExists"))
                    );
                } else {
                    return Promise.resolve();
                }
            }
        );
    };

    save = () => {
        const {
            form: { validateFields },
            ajaxManager,
            onUserCreated,
        } = this.props;

        validateFields({ force: true }, (errors, data) => {
            if (errors) {
                return;
            }

            data.settings.includeUntaggedPrograms = true;
            data.settings.includeAllTaggedPrograms = true;
            data.settings.batchNotifications = true;
            if (data.settings.passwordAuthenticationDisabled) {
                data.inviteUser = false;
            }

            ajaxManager
                .ajax<UserWithSettingsResource, UserCreatorStatus>(
                    () => postUsers(data),
                    {
                        component: this,
                        initialStatusValue: "DEFAULT",
                        inFlightStatus: { type: "SUBMITTING" },
                        getErrorStatus: (_) => {
                            return { type: "DEFAULT" };
                        },
                        getSuccessStatus: (_) => {
                            return { type: "DEFAULT" };
                        },
                    }
                )
                .then((userWithSettings) => {
                    onUserCreated(toUserOverview(userWithSettings.user));
                });
        });
    };
}

export default FormCreate<CreateUserData, UserCreatorFormProps>()(
    withI18n<UserCreatorFormProps>(UserCreatorForm)
);
